import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import '../styles/pages/_modules.scss';
import { Sparkles, Utensils, Monitor, Heart, DollarSign, Briefcase, Shield, CheckCircle } from 'lucide-react';
// Import image using require to handle spaces in filename
const spiritualWellnessImage = require('../assets/Understanding Spiritual Wellness.png');

interface ModuleCard {
  id: string;
  title: string;
  category: 'spiritualM1' | 'spiritualM2' | 'spiritualM3' | 'spiritualM4' | 'spiritualM5' | 'spiritualM6' | 'spiritualM7';
  image: string;
  icon: React.ReactNode;
  featured?: boolean;
}

interface ModuleProgress {
  moduleId: string;
  currentStep: number;
  completed: boolean;
}

const SpiritualResourcesPage: React.FC = () => {
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [progress, setProgress] = useState<ModuleProgress[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchProgress = async () => {
      if (!user || !token) return;

      try {
        // Get progress for all modules
        const moduleIds = ['spiritualM1', 'spiritualM2', 'spiritualM3', 'spiritualM4', 'spiritualM5', 'spiritualM6', 'spiritualM7', 'spiritual/quiz'];
        const responses = await Promise.all(
          moduleIds.map(moduleId =>
            fetch(`${API_CONFIG.ENDPOINTS.MODULE_PROGRESS}/${moduleId}`, {
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              credentials: 'include'
            })
          )
        );

        const results = await Promise.all(
          responses.map(async (response, index) => {
            if (response.ok) {
              const data = await response.json();
              return {
                moduleId: moduleIds[index],
                ...data.data
              };
            }
            return {
              moduleId: moduleIds[index],
              currentStep: 0,
              completed: false
            };
          })
        );

        setProgress(results);
      } catch (error) {

      } finally {
        setIsLoading(false);
      }
    };

    fetchProgress();
  }, [user, token]);

  const handleModuleClick = (moduleId: string) => {
    switch (moduleId) {
      case 'm1':
        navigate('/panel/learn/spiritual-module-1');
        break;
      case 'm2':
        navigate('/panel/learn/spiritual-module-2');
        break;
      case 'm3':
        navigate('/panel/learn/spiritual-module-3');
        break;
      case 'm4':
        navigate('/panel/learn/spiritual-module-4');
        break;
      case 'm5':
        navigate('/panel/learn/spiritual-module-5');
        break;
      case 'm6':
        navigate('/panel/learn/spiritual-module-6');
        break;
      case 'm7':
        navigate('/panel/learn/spiritual-module-7');
        break;
      default:
        console.log(`Module ${moduleId} navigation not yet implemented`);
    }
  };

  const modules: ModuleCard[] = [
    {
      id: 'm1',
      title: 'Understanding Spiritual Wellness',
      category: 'spiritualM1',
      image: spiritualWellnessImage.default || spiritualWellnessImage, // Handle both ESM and CommonJS
      icon: <Sparkles size={24} />,
    },
    {
      id: 'm2',
      title: 'Connectedness – Building Relationship with Life',
      category: 'spiritualM2',
      image: spiritualWellnessImage.default || spiritualWellnessImage, // Handle both ESM and CommonJS
      icon: <Utensils size={24} />,
    },
    {
      id: 'm3',
      title: 'Mindfulness – Cultivating Presence and Awareness',
      category: 'spiritualM3',
      image: spiritualWellnessImage.default || spiritualWellnessImage, // Handle both ESM and CommonJS
      icon: <Heart size={24} />,
    },
    {
      id: 'm4',
      title: 'Compassion – Expanding the Heart',
      category: 'spiritualM4',
      image: spiritualWellnessImage.default || spiritualWellnessImage, // Handle both ESM and CommonJS
      icon: <Monitor size={24} />,
    },
    {
      id: 'm5',
      title: 'Gratitude – Seeing Life as a Gift',
      category: 'spiritualM5',
      image: spiritualWellnessImage.default || spiritualWellnessImage, // Handle both ESM and CommonJS
      icon: <DollarSign size={24} />,
    },
    {
      id: 'm6',
      title: 'Inner Peace – Finding Harmony Amid Chaos',
      category: 'spiritualM6',
      image: spiritualWellnessImage.default || spiritualWellnessImage, // Handle both ESM and CommonJS
      icon: <Briefcase size={24} />,
    },
    {
      id: 'm7',
      title: 'Integrating Spiritual Growth in Coaching Practice',
      category: 'spiritualM7',
      image: spiritualWellnessImage.default || spiritualWellnessImage, // Handle both ESM and CommonJS
      icon: <Shield size={24} />,
    }
  ];

  const allRegularModules = modules.filter(module => !module.featured);

  // All modules are regular modules in this component

  if (isLoading) {
    return (
      <div className={`resources-page ${isDarkMode ? 'dark' : ''}`} style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh'
      }}>
        <p>Loading your progress...</p>
      </div>
    );
  }

  return (
    <div className={`resources-page ${isDarkMode ? 'dark' : ''}`}>
      <div className="module-resources_container">
        {/* Header Section */}
        <div className="module-resources_header">
          <div className="module-resources_header-content">
            <div className="module-resources_header-text">
              <h2>Spiritual Resources</h2>
            </div>
          </div>
        </div>

        {/* Modules Grid */}
        <div className="module-resources_modules">
          <div className="module-resources_modules-grid">
            {allRegularModules.length > 0 ? 
              allRegularModules.map((module: ModuleCard) => {
                const moduleProgress = progress.find(p => p.moduleId === module.category);
                const isCompleted = moduleProgress?.completed || false;
                return (
                  <div 
                    key={module.id} 
                    className={`module-resources_module-card ${isCompleted ? 'completed' : ''}`}
                    onClick={() => handleModuleClick(module.id)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        handleModuleClick(module.id);
                      }
                    }}
                  >
                    <div className="module-resources_module-image">
                      <img src={module.image} alt={module.title} />
                      {isCompleted && (
                        <div className="completion-overlay">
                          <CheckCircle 
                            className="check-icon"
                            strokeWidth={1.5}
                            absoluteStrokeWidth
                          />
                        </div>
                      )}
                    </div>
                    <div className="module-resources_module-content">
                      <h3 className="module-resources_module-title">{module.title}</h3>
                    </div>
                  </div>
                );
              })
              : <p>No modules found.</p>
            }
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpiritualResourcesPage;

